import { applyOverridesFactory, BaseClientEntities, Category } from '@wix/add-panel-common';
import type { Utils } from '../../AddPanel/UtilsProvider';
import type { EvaluateCondition } from '../../AddPanel/utils/evaluateCondition';
import type { ConditionHelpers } from '../../AddPanel/UtilsProvider/UtilsProvider.types';
import { filterOutEntitiesByConditions } from './common';
import { transformCategories } from './categories';
import { transformItems } from './items';
import { transformSections } from './sections';
import { transformGroups } from './groups';

const applyOverrides = applyOverridesFactory({ isApplyingOverridesOnClient: true });

export const transformEntitiesToCategoriesList = ({
  entities,
  evaluateCondition,
  conditionHelpers,
  hasSectionStyle,
}: {
  entities: BaseClientEntities;
  evaluateCondition: EvaluateCondition;
  conditionHelpers: ConditionHelpers;
  hasSectionStyle: Utils['hasSectionStyle'];
}): Category[] => {
  const {
    sections: _sections = [],
    groups: _groups = [],
    categories: _categories = [],
    items: _items = [],
    categoriesOverrides = [],
    sectionsOverrides = [],
    groupsOverrides = [],
    itemsOverrides = [],
  } = entities;

  const filterOutEntities = filterOutEntitiesByConditions(evaluateCondition);

  const filteredCategories = filterOutEntities(_categories);
  const filteredCategoriesOverrides = filterOutEntities(categoriesOverrides);
  const filteredSections = filterOutEntities(_sections);
  const filteredSectionsOverrides = filterOutEntities(sectionsOverrides);
  const filteredGroups = filterOutEntities(_groups);
  const filteredGroupsOverrides = filterOutEntities(groupsOverrides);
  const filteredItems = filterOutEntities(_items);
  const filteredItemsOverrides = filterOutEntities(itemsOverrides);

  // apply categories overrides
  // entity = category
  // child = group
  const [categories, groupsAfterCategoriesOverrides] = applyOverrides({
    entityList: filteredCategories,
    overridesList: filteredCategoriesOverrides,
    overrideToEntityReferenceFieldName: 'categoryToOverride',
    childEntityList: filteredGroups,
    childToEntityMultiReferenceFieldName: 'categoryIds',
    childToOverrideMultiReferenceFieldName: 'categoriesOverridesIds',
  });

  // apply groups overrides
  // parent = category
  // entity = group
  // child = section
  const [groups, sectionsAfterGroupsOverrides] = applyOverrides({
    entityList: groupsAfterCategoriesOverrides,
    overridesList: filteredGroupsOverrides,
    overrideToEntityReferenceFieldName: 'groupToOverride',
    childEntityList: filteredSections,
    childToEntityMultiReferenceFieldName: 'groupIds',
    childToOverrideMultiReferenceFieldName: 'groupOverrideIds',
  });

  // apply sections overrides
  // parent = group
  // entity = section
  // child = item
  const [sections, itemsAfterSectionsOverrides] = applyOverrides({
    entityList: sectionsAfterGroupsOverrides,
    overridesList: filteredSectionsOverrides,
    overrideToEntityReferenceFieldName: 'sectionToOverride',
    childEntityList: filteredItems,
    childToEntityMultiReferenceFieldName: 'sectionIds',
    childToOverrideMultiReferenceFieldName: 'sectionOverrideIds',
    overrideToParentMultiReferenceFieldName: 'applyWhenInTheseGroups',
  });

  // apply items overrides
  // parent = section
  // entity = item
  const [items] = applyOverrides({
    entityList: itemsAfterSectionsOverrides,
    overridesList: filteredItemsOverrides,
    overrideToEntityReferenceFieldName: 'itemToOverride',
    childEntityList: [],
    childToEntityMultiReferenceFieldName: '',
    childToOverrideMultiReferenceFieldName: '',
    entityToParentMultiReferenceFieldName: 'sectionIds',
  });

  const transformedItems = transformItems(items);

  const sectionsWithItems = transformSections(sections, transformedItems, {
    isSectionAvailable: conditionHelpers.isSectionAvailable,
    hasSectionStyle,
  });

  const groupsWithSections = transformGroups(groups, sectionsWithItems);

  const categoriesWithGroups = transformCategories(categories, groupsWithSections, conditionHelpers);

  return categoriesWithGroups;
};
